import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { GraduationCap, ArrowRight, Check, Award, CalendarClock, ShieldCheck, Lock } from "lucide-react";

const WEEKS = [
  { w: 1, t: "Death Investigation 101 — Learn the System", s: "How an investigator decides, before anything else, whether a death is theirs to work." },
  { w: 2, t: "Death or Murder? — Manner of Death", s: "The call that decides whether a death is ever investigated at all." },
  { w: 3, t: "The Scene Tells Stories — Evidence & Observation", s: "Reading the one witness that can't lie — but can be erased." },
  { w: 4, t: "Wounds as Language, Part 1 — Sharp Force", s: "A wound is a sentence. Learn to read it." },
  { w: 5, t: "Wounds as Language, Part 2 — Blunt Force", s: "What patterns reveal about the weapon." },
  { w: 6, t: "Death's Timing — Body Changes Over Time", s: "The body keeps a clock — and it catches inconsistencies." },
  { w: 7, t: "When Stories Don't Match — Red Flags", s: "The gap between the account and the evidence." },
  { w: 8, t: "Behavioral Clues — Introduction to Profiling", s: "What a scene reveals about how it happened." },
  { w: 9, t: "Specialized Investigations — High-Risk Cases", s: "The cases the system is built to miss." },
  { w: 10, t: "Case Synthesis I — Intake, Jurisdiction & Scene", s: "Capstone, part 1: the first hour of a real case." },
  { w: 11, t: "Case Synthesis II — Wounds, Timing & Inconsistencies", s: "Capstone, part 2: the body and the clocks talk back." },
  { w: 12, t: "Case Synthesis III — Graduation", s: "Capstone, part 3: analyze it, and defend your conclusions." },
];

const INCLUDES = [
  "12 weekly video + reading lessons, released one per week",
  "Auto-graded quizzes and branching case scenarios",
  "A 3-part 'Lakeside House' capstone investigation",
  "Per-module discussion with Christine and fellow members",
  "Badges as you progress — and a Certified Detective badge at the end",
  "Then-vs-Now notes: how the work was done decades ago vs. today",
];

export default function CoursePage() {
  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src="/course-bg.jpg" alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-charcoal/90" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2 label-mono mb-5 px-3 py-1.5 border border-gold/40 rounded-sm bg-charcoal/70 text-gold">
            <GraduationCap className="w-4 h-4" /> New · 12-week program
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-white">
            The Homicide<br /><span className="text-gold">Investigations Course</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/65 leading-relaxed max-w-2xl mx-auto">
            Learn to work a death investigation the way a real detective does — decision by decision.
            From the first call to the courtroom, taught in Christine Burke's own voice across twelve
            weekly lessons, ending in your Certified Detective badge.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/pricing" data-testid="course-join-cta" className="group inline-flex items-center gap-2 px-6 py-3.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
              Join as a member <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href="https://www.youtube.com/@helpsolvethecase" target="_blank" rel="noopener noreferrer" data-testid="course-youtube-cta" className="px-6 py-3.5 border border-white/15 text-white rounded-sm hover:border-gold/40 transition-colors">
              Watch on YouTube
            </a>
          </div>
          <p className="mt-6 text-sm text-gold font-mono flex items-center justify-center gap-2">
            <CalendarClock className="w-4 h-4" /> Open to members now · New lesson every week
          </p>
          <p className="mt-2 text-xs text-white/40 font-mono flex items-center justify-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-gold" /> Educational case-study training — presumed innocent, never accusatory.
          </p>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-16">
        <div className="label-mono mb-3 text-gold">What's included</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {INCLUDES.map((t) => (
            <div key={t} className="card-surface rounded-md p-4 flex items-start gap-3">
              <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" />
              <span className="text-sm text-white/80 leading-relaxed">{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CURRICULUM */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-16">
        <div className="label-mono mb-3 text-gold">The 12-week curriculum</div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white mb-8 max-w-2xl">
          One new lesson every week, from first call to courtroom.
        </h2>
        <div className="space-y-2.5">
          {WEEKS.map((m, i) => (
            <motion.div key={m.w} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
              className="card-surface rounded-md p-4 flex items-start gap-4" data-testid={`course-week-${m.w}`}>
              <div className="shrink-0 w-11 h-11 rounded-sm bg-gold/10 border border-gold/20 flex items-center justify-center font-display font-bold text-gold">
                {m.w}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-base font-semibold text-white leading-snug">{m.t}</h3>
                <p className="mt-1 text-sm text-white/55 leading-relaxed">{m.s}</p>
              </div>
              <span className="label-mono text-[10px] text-white/30 hidden sm:block pt-1">Week {m.w}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-20">
        <div className="card-surface rounded-lg p-10 sm:p-14 text-center grain border border-gold/25">
          <Award className="w-10 h-10 text-gold mx-auto mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Earn your Certified Detective badge.</h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">
            The Homicide Investigations Course is included with membership — and it's open now. Join to start
            the lessons today, and get every case file, the Bullpen, and the Case Review Library too.
          </p>
          <Link to="/pricing" data-testid="course-final-cta" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
            Join as a member — $4.99/month <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-4 text-xs text-white/40 font-mono flex items-center justify-center gap-2">
            <Lock className="w-3.5 h-3.5" /> Course content unlocks for active members · cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
